import type {
  CellKey,
  ResolutionAction,
  ResolutionState,
  SessionIds,
} from './types';
import { cellKey, parseCellKey } from './cellKey';
import { cellFactsOf } from './estimateCounts';

export function initialState(session: SessionIds): ResolutionState {
  return {
    session,
    serverDecisionKeys: [],
    serverDecisions: {},
    pages: {},
    periodSpans: [],
    cellFacts: {},
    staleResolved: {},
    decisions: {},
    reopened: [],
    autoRebased: [],
    reopenedCarrier: [],
    noDecisionNeeded: [],
    structural: { blocked: false, message: '' },
    ui: {
      section: 'needs_decision',
      inputKey: null,
      expandedRowId: null,
      changedUnderneathOnly: false,
      periodRange: 'needs_decision',
      requestedPages: {},
    },
  };
}

/** `stale` without the cells of `rows`: those rows were just fetched, carrying the current decisions. */
function withoutRows(
  stale: Record<CellKey, true>,
  rows: { Input_RowID: string }[]
): Record<CellKey, true> {
  const keys = Object.keys(stale) as CellKey[];
  if (keys.length === 0 || rows.length === 0) {
    return stale;
  }
  const loaded = new Set(rows.map((row) => row.Input_RowID));
  const kept = keys.filter((key) => !loaded.has(parseCellKey(key).inputRowId));
  if (kept.length === keys.length) {
    return stale;
  }
  return Object.fromEntries(kept.map((key) => [key, true])) as Record<
    CellKey,
    true
  >;
}

export function resolutionReducer(
  state: ResolutionState,
  action: ResolutionAction
): ResolutionState {
  switch (action.type) {
    case 'session/loaded': {
      if (
        state.session.baseSubmissionId === action.baseSubmissionId &&
        state.session.currentSubmissionId === action.currentSubmissionId
      ) {
        return state;
      }
      return {
        ...state,
        session: {
          ...state.session,
          baseSubmissionId: action.baseSubmissionId,
          currentSubmissionId: action.currentSubmissionId,
        },
      };
    }

    case 'counts/loaded': {
      const newServerDecisionKeys =
        action.serverDecisionKeys ?? state.serverDecisionKeys;
      return {
        ...state,
        serverCounts: action.counts,
        serverDecisionKeys: newServerDecisionKeys,
      };
    }

    case 'page/loaded': {
      const currentPage = state.pages[action.section];
      // Spans are replaced on every load; absent means none.
      const periodSpans =
        action.periodSpans ??
        (state.periodSpans.length === 0 ? state.periodSpans : []);
      if (currentPage === action.page && periodSpans === state.periodSpans) {
        return state;
      }
      return {
        ...state,
        periodSpans,
        pages: {
          ...state.pages,
          [action.section]: action.page,
        },
        cellFacts: {
          ...state.cellFacts,
          ...cellFactsOf({ [action.section]: action.page }),
        },
        staleResolved: withoutRows(state.staleResolved, action.page.rows),
      };
    }

    case 'decision/set': {
      const key = cellKey(
        action.decision.input_row_id,
        action.decision.date_id
      );
      const existing = state.decisions[key];
      if (existing === action.decision) {
        return state;
      }
      return {
        ...state,
        decisions: {
          ...state.decisions,
          [key]: action.decision,
        },
      };
    }

    case 'decision/setMany': {
      const newDecisions = { ...state.decisions };
      let changed = false;
      for (const decision of action.decisions) {
        const key = cellKey(decision.input_row_id, decision.date_id);
        if (newDecisions[key] !== decision) {
          newDecisions[key] = decision;
          changed = true;
        }
      }
      if (!changed) {
        return state;
      }
      return {
        ...state,
        decisions: newDecisions,
      };
    }

    case 'decision/remove': {
      if (!(action.key in state.decisions)) {
        return state;
      }
      const newDecisions = { ...state.decisions };
      delete newDecisions[action.key];
      return {
        ...state,
        decisions: newDecisions,
        staleResolved: { ...state.staleResolved, [action.key]: true },
      };
    }

    case 'decision/removeMany': {
      let hasAny = false;
      for (const key of action.keys) {
        if (key in state.decisions) {
          hasAny = true;
          break;
        }
      }
      if (!hasAny) {
        return state;
      }
      const newDecisions = { ...state.decisions };
      const staleResolved = { ...state.staleResolved };
      for (const key of action.keys) {
        if (key in newDecisions) {
          staleResolved[key] = true;
        }
        delete newDecisions[key];
      }
      return {
        ...state,
        decisions: newDecisions,
        staleResolved,
      };
    }

    case 'rebase/applied': {
      return {
        ...state,
        decisions: action.result.decisions,
        serverDecisions: action.result.decisions,
        reopened: action.result.reopened,
        autoRebased: action.result.autoRebased,
        reopenedCarrier: action.result.carrier,
        noDecisionNeeded: action.result.noDecisionNeeded,
        session: {
          ...state.session,
          currentSubmissionId: action.result.session.currentSubmissionId,
          previousCurrentSubmissionId:
            action.result.session.previousCurrentSubmissionId,
        },
        serverCounts: action.result.serverCounts,
      };
    }

    case 'structural/set': {
      if (
        state.structural.blocked === action.blocked &&
        state.structural.message === action.message
      ) {
        return state;
      }
      return {
        ...state,
        structural: { blocked: action.blocked, message: action.message },
      };
    }

    case 'ui/section': {
      if (state.ui.section === action.section) {
        return state;
      }
      return {
        ...state,
        ui: {
          ...state.ui,
          section: action.section,
        },
      };
    }

    case 'ui/input': {
      if (state.ui.inputKey === action.inputKey) {
        return state;
      }
      // A new filter is a new row set, so every section starts again at page 1.
      return {
        ...state,
        ui: {
          ...state.ui,
          inputKey: action.inputKey,
          requestedPages: {},
        },
      };
    }

    case 'ui/page': {
      if (!Number.isInteger(action.page) || action.page < 1) {
        return state;
      }
      if ((state.ui.requestedPages[action.section] ?? 1) === action.page) {
        return state;
      }
      return {
        ...state,
        ui: {
          ...state.ui,
          requestedPages: {
            ...state.ui.requestedPages,
            [action.section]: action.page,
          },
        },
      };
    }

    case 'ui/expand': {
      const newRowId =
        state.ui.expandedRowId === action.rowId ? null : action.rowId;
      if (state.ui.expandedRowId === newRowId) {
        return state;
      }
      return {
        ...state,
        ui: {
          ...state.ui,
          expandedRowId: newRowId,
        },
      };
    }

    case 'ui/changedUnderneathOnly': {
      if (state.ui.changedUnderneathOnly === action.value) {
        return state;
      }
      return {
        ...state,
        ui: {
          ...state.ui,
          changedUnderneathOnly: action.value,
          requestedPages: {},
        },
      };
    }

    case 'ui/periodRange': {
      if (state.ui.periodRange === action.mode) {
        return state;
      }
      return {
        ...state,
        ui: {
          ...state.ui,
          periodRange: action.mode,
        },
      };
    }
  }
}
